import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, EmptyState, Eyebrow, Field, Input, Spinner } from '@busbook/ui';
import { useCustomer } from '../lib/customer-session.js';
import { useTravellers } from '../lib/travellers.js';
import { useFeature } from '../lib/tenant.js';
import { useT } from '../i18n/index.js';

/**
 * The people this passenger books for.
 *
 * Exists so a name can be added before a booking rather than during one. Adding a traveller
 * mid-checkout means stopping to type the thing you were trying to avoid typing, which is
 * the opposite of the point.
 */
export function Travellers() {
  const t = useT();
  const { user, loading: sessionLoading } = useCustomer();
  const enabled = useFeature('travellers');
  const { travellers, loading, save, remove } = useTravellers(Boolean(user) && enabled);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!fullName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await save({
        fullName: fullName.trim(),
        phone: phone.trim(),
        idNumber: idNumber.trim(),
        email: email.trim(),
      });
      setFullName('');
      setPhone('');
      setIdNumber('');
      setEmail('');
    } catch {
      setError(t('tr.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (sessionLoading) {
    return (
      <section className="grid place-items-center px-4 py-16">
        <Spinner label={t('c.loading')} />
      </section>
    );
  }

  // A guest has nowhere to save to. Offering sign-in is the honest answer; showing an empty
  // list they can add to and lose on the next visit is not.
  if (!user) {
    return (
      <section className="px-4 py-6">
        <Eyebrow>{t('tr.title')}</Eyebrow>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">{t('tr.heading')}</h1>
        <div className="mt-5">
          <Alert tone="info" title={t('tr.signInNeeded')}>
            {t('tr.signInHint')}{' '}
            <Link to="/auth/login" className="underline underline-offset-4">
              {t('c.signIn')}
            </Link>
          </Alert>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 py-6">
      <Eyebrow>{t('tr.title')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{t('tr.heading')}</h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {t('tr.hint')}
      </p>

      {loading ? (
        <div className="grid place-items-center py-12">
          <Spinner label={t('c.loading')} />
        </div>
      ) : travellers.length === 0 ? (
        <div className="mt-5">
          <EmptyState title={t('tr.none')} description={t('tr.noneHint')} />
        </div>
      ) : (
        <ul className="mt-5 flex flex-col gap-2">
          {travellers.map((traveller) => (
            <li
              key={traveller.id}
              className="flex items-center gap-3 rounded-xl border p-3"
              style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{traveller.fullName}</p>
                {(traveller.phone || traveller.idNumber || traveller.email) && (
                  <p className="truncate text-xs" style={{ color: 'var(--color-muted)' }}>
                    {[traveller.email, traveller.phone, traveller.idNumber]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="trip-action"
                onClick={() => void remove(traveller.id)}
              >
                {t('tr.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={submit}
        className="mt-7 rounded-xl border p-4"
        style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
      >
        <Eyebrow>{t('tr.add')}</Eyebrow>
        <div className="mt-3 flex flex-col gap-3">
          <Field label={t('k.fullName')} htmlFor="tr-name">
            <Input
              id="tr-name"
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </Field>
          <Field label={t('c.phone')} htmlFor="tr-phone" hint={t('tr.optional')}>
            <Input
              id="tr-phone"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          {/* The one field here that is actually sent to, which is why it is validated as
              an address rather than stored as typed. */}
          <Field label={t('tr.email')} htmlFor="tr-email" hint={t('tr.emailHint')}>
            <Input
              id="tr-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          {/* Not verified against anything — it is the number an operator asks for at
              boarding, carried so the passenger does not have to remember it. */}
          <Field label={t('tr.idNumber')} htmlFor="tr-id" hint={t('tr.idHint')}>
            <Input id="tr-id" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
          </Field>
          {error && (
            <p role="alert" className="text-sm font-medium" style={{ color: 'var(--signal-cancelled)' }}>
              {error}
            </p>
          )}
          <Button type="submit" variant="secondary" block loading={busy}>
            {t('tr.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
